// This class allows you to enqueue asyncronous requests with an optional followup function
// to be ran after the enqueued request completes.
//
// Example
// -------
// ```ts
// const queue = new RequestQueue<number>();
// queue.enqueueRequest(
//   async () => {
//     const data = await fetch('/api/some_endpoint');
//     console.log('Fetched data:', await data.json());
//   },
//   () => console.log('Request completed!')
// );
//

type FollowupFn = (() => void) | undefined;
export class RequestQueue<T> {
  queue: Array<[() => Promise<T>, FollowupFn]>;
  working: boolean;

  constructor() {
    this.queue = [];
    this.working = false;
  }

  // Enqueues a request to the end of the queue
  enqueueRequest(request: () => Promise<any>, followup?: FollowupFn) {
    this.queue.push([request, followup]);

    if (!this.working) {
      this.working = true;
      this.doWork();
    }
  }

  // Enqueues a request to the start of the queue
  prependRequest(request: () => Promise<any>, followup?: FollowupFn) {
    this.queue.unshift([request, followup]);

    if (!this.working) {
      this.working = true;
      this.doWork();
    }
  }

  async doWork() {
    // ! is ok here because we only call doWork when there's work to be done
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const [request, followup] = this.queue.shift()!;

    await request();
    followup && followup();

    if (this.queue.length > 0) {
      this.doWork();
    } else {
      this.working = false;
    }
  }
}
